import { update } from 'ramda'
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { breakpoints } from '@/styles'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import { createModal, useModalize } from '@/components/modalize/mui'
import { ProgressWizard, WizardContent } from '@/components/wizard'
import scrollTop from '@/utils/scroll-top'
import { Box } from '@/components/structure'
import AddendumView from './addendum-view'

const CONTENT_ID = 'modal-wizard-content'

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const CloseButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 50px;
  margin-top: -5px;
`

const ScrollWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 25px;
  min-width: 640px;

  // use after for bottom padding due to Firefox bug:
  // https://stackoverflow.com/questions/29986977
  :after {
    content: '';
    display: block;
    padding-bottom: 50px;
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-width: unset;
  }
`

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 640px;
`

const AddendumModalWizard = ({ contractId, data, activeStep, onChange, variables }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(activeStep)
  const [addendums, setAddendums] = useState(data)
  const [buttonLabel, setButtonLabel] = useState('')

  const getButtonLabel = useCallback(() => {
    const incompleteSteps = addendums.filter(({ complete }) => !complete).length
    const remainingSteps = addendums.filter(
      step => !step.complete || currentStep === step.id
    ).length

    if (incompleteSteps < 2 && remainingSteps === 1) {
      setButtonLabel(t('common.done', 'Done'))
    } else {
      setButtonLabel(t('b.contract.view.next_addendum.button', 'Next Addendum'))
    }
  }, [addendums, currentStep, t])

  useEffect(() => {
    getButtonLabel()
  }, [getButtonLabel])

  const handleChange = item => {
    const itemIndex = addendums.findIndex(addendum => item.id === addendum.id)
    const updatedAddendum = {
      ...item,
      complete: !item.complete
    }
    setAddendums(update(itemIndex, updatedAddendum, addendums))
    onChange(item)
  }

  const resetScroll = scrollTop(CONTENT_ID)

  const setStep = step => {
    setCurrentStep(step)
    resetScroll()
  }

  const getStepKey = step => {
    return step.id
  }

  const getSteps = () => {
    const steps = addendums.filter(step => !step.complete || currentStep === getStepKey(step))

    const stepIndex = steps.findIndex(item => getStepKey(item) === currentStep)
    const before = steps.slice(0, stepIndex)
    const after = steps.slice(stepIndex)

    return [...after, ...before]
  }

  const goToNextStep = steps => {
    if (steps.length === 1) {
      close()
    } else {
      const itemIndex = steps.findIndex(item => getStepKey(item) === currentStep)
      const newStep = steps[itemIndex + 1]
      setStep(getStepKey(newStep))
    }
  }

  const renderStep = item => {
    return (
      <AddendumView
        contractId={contractId}
        key={item.id}
        addendum={item}
        onChange={addendum => handleChange(addendum)}
        variables={variables}
      />
    )
  }

  const renderCurrentStep = (step, index, arr) => {
    const key = getStepKey(step)
    return (
      <Step index={key} key={key}>
        {renderStep(step, index, arr)}
      </Step>
    )
  }

  const isButtonDisabled = () => {
    const step = addendums.find(item => getStepKey(item) === currentStep) || {}
    return !step.complete
  }

  const getProgress = () => {
    const completed = addendums.filter(item => item.complete)
    return (completed.length / addendums.length) * 100
  }

  const validSteps = getSteps()

  return (
    <>
      <HeaderWrapper>
        <ProgressWizard progress={getProgress()} />
        <CloseButtonWrapper>
          <CloseButton onClick={close} />
        </CloseButtonWrapper>
      </HeaderWrapper>
      <ScrollWrapper id={CONTENT_ID}>
        <WizardContent currentStep={currentStep}>
          {validSteps.map((step, index, arr) => renderCurrentStep(step, index, arr))}
        </WizardContent>
        <Box top={28} justify="center">
          <Button
            theme="primary"
            onClick={() => goToNextStep(validSteps)}
            disabled={isButtonDisabled()}
          >
            {buttonLabel}
          </Button>
        </Box>
      </ScrollWrapper>
    </>
  )
}

AddendumModalWizard.defaultProps = {
  data: [],
  currentStep: null
}

export const showAddendumModalWizard = createModal(AddendumModalWizard, {
  width: 'auto',
  maxWidth: '805px',
  showClose: false
})
